import * as viewReducer from "containers/View/reducer";
import * as loginReducer from "containers/Login/reducer";

import * as homeReducer from "containers/Home/reducer";
import * as deployReducer from "containers/Deploy/reducer";
import * as deploymentsReducer from "containers/Deployments/reducer";
import * as runReducer from "containers/Run/reducer";
import * as accountsReducer from "containers/Accounts/reducer";
import * as schemaMonitorReducer from "containers/SchemaMonitor/reducer";
import * as languageModelArtifactReducer from "containers/LanguageModelArtifact/reducer";
import * as fineTuningReducer from "containers/FineTuning/reducer";

import { getItem } from "containers/Login/reducer";

import { isEnabled } from "utils/featureFlags";
import * as nextReducer from "containers/Next/NextReducer";

const UPDATE_FIELD = "global/UPDATE_FIELD";

const getParameterByName = (name, url) => {
  if (!url) url = window.location.href;
  name = name.replace(/[\[\]]/g, "\\$&");
  var regex = new RegExp("[?&]" + name + "(=([^&#]*)|&|#|$)"),
    results = regex.exec(url);
  if (!results) return null;
  if (!results[2]) return "";
  return decodeURIComponent(results[2].replace(/\+/g, " "));
};

const initialState = {
  pollingIntervalId: undefined,
  isDatasetsLoading: true,
  isJobsLoading: false,
  isBlueprintsLoading: true,
  isBlueprintCreating: false,
  isDeploymentsLoading: true,
  isModelGroupsLoading: true,
  isOptimizersLoading: true,
  isReportsLoading: true,
  isSchemaMonitorsLoading: false,
  isDataUploading: false,
  isProjectsLoading: true,
  isArtifactsLoading: false,
  isAnalyticsLoading: false,
  isTemplateLoading: false,
  isCreatingFocusedDataset: false,
  isCreatingStepByStepAgent: false,
  datasetOffset: 10,
  projectOffset: 50,
  runOffset: 20,
  isLeftPanelOpen: false,
  isTableauMode: false,
  isBusinessMode: isEnabled("aible-business"),
  isDataMode: isEnabled("aible-data"),
  currentRunId: undefined,
  isConsumerMode: getParameterByName("collaborate") != null,
  pendingDeployments: {},
  activeBlueprintId: "new",
  createFromProject: false,
  showProjectHelp: getItem ? !getItem(undefined, "projectHideHelp") : false,
  isCreatingExecutor: false,
  isGoogleAuthed: false,
  projectFolderId: undefined,
  lookerDashboardId: undefined,
  lastOpenAIChat: 0
};

const GOOGLE_DEBUG = false;

function globalReducer(state = initialState, action) {
  switch (action.type) {
    case UPDATE_FIELD:
      return {
        ...state,
        [action.key]: action.data
      };
    default:
      return state;
  }
}

export const ROLE_VALUES = {
  VIEWER: { label: "Viewer", value: "VIEWER" },
  CONTRIBUTOR: { label: "Collaborator", value: "CONTRIBUTOR" },
  EDITOR: { label: "Editor", value: "EDITOR" },
  OWNER: { label: "Owner", value: "OWNER" }
};

export const ROLE_OPTIONS = [
  // ROLE_VALUES.VIEWER,
  ROLE_VALUES.CONTRIBUTOR,
  ROLE_VALUES.EDITOR,
  ROLE_VALUES.OWNER
];

export const FOLDER_ROLES = [ROLE_VALUES.VIEWER, ROLE_VALUES.EDITOR, ROLE_VALUES.OWNER];

export const PAGE_TYPE = {
  LOGIN: "LOGIN",
  REGISTRATION: "REGISTRATION",
  RESET: "RESET_PASSWORD",
  HOME: "HOME",
  PROJECT: "PROJECT",
  SETTINGS: "SETTINGS",
  MARKETPLACE: "MARKETPLACE",
  DEFAULT: "DEFAULT"
};

export function setPageTitle(type, id, name) {
  let pageTitle = "Aible";

  switch (type) {
    case PAGE_TYPE.LOGIN:
      pageTitle += " - Login";
      break;
    case PAGE_TYPE.REGISTRATION:
      pageTitle += " - Register Account";
      break;
    case PAGE_TYPE.RESET:
      pageTitle += " - Reset Password";
      break;
    case PAGE_TYPE.HOME:
      pageTitle += " - Home";
      break;
    case PAGE_TYPE.PROJECT:
      if (id) {
        if (isNaN(id)) {
          pageTitle += " - New Project";
        } else {
          if (name) {
            pageTitle += ` - (${id}) ${name}`;
          } else {
            pageTitle += ` - Project ${id}`;
          }
        }
      } else {
        pageTitle += " - Project";
      }
      break;
    case PAGE_TYPE.SETTINGS:
      pageTitle += " - Settings";
      break;
    case PAGE_TYPE.MARKETPLACE:
      pageTitle += " - Marketplace";
      break;
    default:
  }
  if (document.title !== pageTitle) {
    document.title = pageTitle;
  }
}

/* Selectors */
export function getGlobal(state) {
  return state.global;
}

export function getIsGoogleMode(state) {
  const stateData = getGlobal(state).isGoogleMode;

  if (GOOGLE_DEBUG) {
    const storedMode = getItem(state, "aible_isGoogleMode");

    if (storedMode !== undefined && stateData === undefined) {
      return storedMode;
    }
  }

  return stateData;
}

export function getGoogleSheetsData(state) {
  const stateData = getGlobal(state).googleSheetsData;

  if (GOOGLE_DEBUG) {
    const storedData = getItem(state, "aible_googleSheetsData");

    if (storedData !== undefined && stateData === undefined) {
      try {
        return JSON.parse(storedData);
      } catch (e) {
        console.log(e);
      }
    }
  }

  return stateData;
}

export function getClawActivityLoad(state, id) {
  return getGlobal(state)?.clawActivityLoad?.[id] ?? false;
}

export function getIsSkipNextInit(state) {
  return getGlobal(state)?.isSkipNextInit;
}

export function getIsSkipChatGPTInit(state) {
  return getGlobal(state)?.isSkipChatGPTInit;
}

export function getIsSkipChatGPTReset(state) {
  return getGlobal(state)?.isSkipChatGPTReset;
}

export function getUiState(state) {
  return getGlobal(state)?.uiState;
}

export function getEnvironment(state) {
  return getGlobal(state).environment;
}

export function getCreateFromProject(state) {
  return getGlobal(state).createFromProject;
}

export function getIsDatasetsLoading(state) {
  return getGlobal(state).isDatasetsLoading;
}

export function getIsRecipesLoading(state) {
  return getGlobal(state).isRecipesLoading;
}

export function getIsJobsLoading(state) {
  return getGlobal(state).isJobsLoading;
}

export function getLookerDashboardId(state) {
  return getGlobal(state).lookerDashboardId;
}

export function getIsBlueprintsLoading(state) {
  return getGlobal(state).isBlueprintsLoading;
}

export function getIsDeploymentsLoading(state) {
  return getGlobal(state).isDeploymentsLoading;
}

export function getIsModelGroupsLoading(state) {
  return getGlobal(state).isModelGroupsLoading;
}

export function getIsSchemaMonitorLoading(state) {
  return getGlobal(state).isSchemaMonitorLoading;
}

export function getIsArtifactsLoading(state) {
  return getGlobal(state).isArtifactsLoading;
}

export function getIsFineTuningLoading(state) {
  return getGlobal(state).isFineTuningLoading;
}

export function getIsExampleSetsLoading(state) {
  return getGlobal(state).isExampleSetsLoading;
}

export function getProjectFolderId(state) {
  return getGlobal(state).projectFolderId;
}

export function getIsProjectsLoading(state) {
  return getGlobal(state).isProjectsLoading;
}

export function getIsAnalyticsLoading(state) {
  return getGlobal(state).isAnalyticsLoading;
}

export function getIsTemplateLoading(state) {
  return getGlobal(state).isTemplateLoading;
}

export function getIsOptimizersLoading(state) {
  return getGlobal(state).isOptimizersLoading;
}

export function getIsReportsLoading(state) {
  return getGlobal(state).isReportsLoading;
}

export function getProjectOffset(state) {
  return getGlobal(state).projectOffset;
}

export function getDatasetOffset(state) {
  return getGlobal(state).datasetOffset;
}

export function getRunOffset(state) {
  return getGlobal(state).runOffset;
}

export function getIsDataUploading(state) {
  return getGlobal(state).isDataUploading;
}

export function getActiveBlueprintId(state) {
  return getGlobal(state).activeBlueprintId;
}

export function getHoverItem(state) {
  return getGlobal(state).hoverItem;
}

export function getIsLeftPanelOpen(state) {
  return getGlobal(state).isLeftPanelOpen;
}

export function getIsBlueprintCreating(state) {
  return getGlobal(state).isBlueprintCreating;
}

export function getPendingDeployments(state) {
  return getGlobal(state).pendingDeployments;
}

export function getBlueprintId(state) {
  return getGlobal(state).blueprintId;
}

export function getRunId(state) {
  return getGlobal(state).runId;
}

export function getIsDatasetFetching(state) {
  return getGlobal(state).isDatasetFetching || {};
}

export function getIsTableauMode(state) {
  return isEnabled("tableau") || getGlobal(state).isTableauMode || loginReducer.getTableauDataOptions(state);
}

export function getIsBusinessMode(state) {
  return isEnabled("aible-business") || isEnabled("aible-business-simple") || getGlobal(state).isBusinessMode;
}

export function getIsDataMode(state) {
  return isEnabled("aible-data") || getGlobal(state).isDataMode;
}

export function getIsConsumerMode(state) {
  return getGlobal(state).isConsumerMode;
}

export function getCurrentRunId(state) {
  return getGlobal(state).currentRunId;
}

export function getCurrentProjectId(state) {
  return getGlobal(state).currentProjectId;
}

export function getIsShowProjectHelp(state) {
  return getGlobal(state).showProjectHelp;
}

export function getPollingIntervalId(state) {
  return getGlobal(state).pollingIntervalId;
}

export function getIsCreatingExecutor(state) {
  return getGlobal(state).isCreatingExecutor;
}

export function getIsCreatingFocusedDataset(state) {
  return getGlobal(state).isCreatingFocusedDataset;
}

export function getIsCreatingStepByStepAgent(state) {
  return getGlobal(state).isCreatingStepByStepAgent;
}

export function getIsNextLoading(state) {
  return getGlobal(state).isNextLoading ?? true;
}

export function getIsOpeningComparison(state) {
  return getGlobal(state).isOpeningComparison ?? false;
}

export function getIsGoogleAuthed(state) {
  return getGlobal(state).isGoogleAuthed ?? false;
}

export function getLastOpenAIChat(state) {
  return getGlobal(state).lastOpenAIChat;
}

export function getShouldSwitchToGPT(state) {
  if (isEnabled("chat-aible-consumer") && !isEnabled("aible-optimize")) {
    return true;
  }
  return getGlobal(state).shouldSwitchToGPT;
}

/* Actions */
export function updateIsSkipNextInit(id) {
  return {
    type: UPDATE_FIELD,
    data: id,
    key: "isSkipNextInit"
  };
}

export function updateIsSkipChatGPTInit(value) {
  return {
    type: UPDATE_FIELD,
    data: value,
    key: "isSkipChatGPTInit"
  };
}

export function updateIsSkipChatGPTReset(value) {
  return {
    type: UPDATE_FIELD,
    data: value,
    key: "isSkipChatGPTReset"
  };
}

export function updateUiState(val) {
  return {
    type: UPDATE_FIELD,
    data: val,
    key: "uiState"
  };
}

export function updateIsGoogleMode(val) {
  return (dispatch, getState) => {
    if (GOOGLE_DEBUG) {
      dispatch(loginReducer.storeItem("aible_isGoogleMode", val));
    }

    return dispatch({
      type: UPDATE_FIELD,
      data: val,
      key: "isGoogleMode"
    });
  };
}

export function updateClawActivityLoad(id, val) {
  return (dispatch, getState) => {
    const currActivity = getClawActivityLoad(getState(), id) ?? {};
    return dispatch({
      type: UPDATE_FIELD,
      key: "clawActivityLoad",
      data: {
        ...currActivity,
        [id]: val
      }
    });
  };
}

export function updateGoogleSheetsData(data) {
  return (dispatch, getState) => {
    if (GOOGLE_DEBUG) {
      try {
        const dataJSON = JSON.stringify(data);
        dispatch(loginReducer.storeItem("aible_googleSheetsData", dataJSON));
      } catch (e) {
        console.log(e);
      }
    }

    dispatch({
      type: UPDATE_FIELD,
      data: data,
      key: "googleSheetsData"
    });

    dispatch(nextReducer.handleGoogleSheetsSearch());
  };
}

export function postToGoogleSheets(data) {
  return (dispatch, getState) => {
    window.parent.postMessage(
      {
        type: "AIBLE",
        data: data
      },
      "*"
    );
    return Promise.resolve();
  };
}

export function startPolling() {
  return (dispatch, getState) => {
    let intervalId = getPollingIntervalId(getState());
    if (intervalId === undefined) {
      intervalId = setInterval(() => {
        dispatch(homeReducer.pollDatasets());
        dispatch(deployReducer.pollScores());
        dispatch(accountsReducer.pollAccounts());
        dispatch(deploymentsReducer.pollDeployments());
        dispatch(schemaMonitorReducer.pollSchemaMonitors());
        dispatch(runReducer.pollJobs());
      }, 15000);
      dispatch(updatePollingIntervalId(intervalId));
    }
    return intervalId;
  };
}

export function stopPolling() {
  return (dispatch, getState) => {
    const intervalId = getPollingIntervalId(getState());
    clearInterval(intervalId);
    return dispatch(updatePollingIntervalId(undefined));
  };
}

export function updatePollingIntervalId(id) {
  return {
    type: UPDATE_FIELD,
    data: id,
    key: "pollingIntervalId"
  };
}

export function updateProjectFolderId(projectFolderId) {
  return {
    type: UPDATE_FIELD,
    data: projectFolderId,
    key: "projectFolderId"
  };
}

export function updateIsShowProjectHelp(value) {
  return (dispatch, getState) => {
    let retValue = value !== undefined ? value : !getIsShowProjectHelp(getState());
    dispatch({
      type: UPDATE_FIELD,
      data: retValue,
      key: "showProjectHelp"
    });
    if (retValue) {
      dispatch(loginReducer.removeItem("projectHideHelp"));
    } else {
      dispatch(loginReducer.storeItem("projectHideHelp", true));
    }
  };
}

export function updateEnvironment(environment) {
  return {
    type: UPDATE_FIELD,
    data: environment,
    key: "environment"
  };
}

export function updateCreateFromProject(bool) {
  return {
    type: UPDATE_FIELD,
    data: bool,
    key: "createFromProject"
  };
}

export function updateIsDatasetsLoading(isDatasetsLoading) {
  return {
    type: UPDATE_FIELD,
    data: isDatasetsLoading,
    key: "isDatasetsLoading"
  };
}

export function updateIsRecipesLoading(isRecipesLoading) {
  return {
    type: UPDATE_FIELD,
    data: isRecipesLoading,
    key: "isRecipesLoading"
  };
}

export function updateIsJobsLoading(isJobsLoading) {
  return { type: UPDATE_FIELD, data: isJobsLoading, key: "isJobsLoading" };
}

export function updateIsBlueprintsLoading(isBlueprintsLoading) {
  return {
    type: UPDATE_FIELD,
    data: isBlueprintsLoading,
    key: "isBlueprintsLoading"
  };
}

export function updateDatasetOffset(datasetOffset) {
  return { type: UPDATE_FIELD, data: datasetOffset, key: "datasetOffset" };
}

export function updateRunOffset(runOffset) {
  return { type: UPDATE_FIELD, data: runOffset, key: "runOffset" };
}

export function updateActiveBlueprintId(activeBlueprintId) {
  return (dispatch, getState) => {
    if (getActiveBlueprintId(getState()) !== activeBlueprintId) {
      return dispatch({
        type: UPDATE_FIELD,
        data: activeBlueprintId,
        key: "activeBlueprintId"
      });
    }
  };
}

export function updateIsDataUploading(isDataUploading) {
  return { type: UPDATE_FIELD, data: isDataUploading, key: "isDataUploading" };
}

export function updateIsBlueprintCreating(isBlueprintCreating) {
  return {
    type: UPDATE_FIELD,
    data: isBlueprintCreating,
    key: "isBlueprintCreating"
  };
}

export function updatePendingDeployments(pendingDeployments) {
  return {
    type: UPDATE_FIELD,
    data: pendingDeployments,
    key: "pendingDeployments"
  };
}

export function updateIsDeploymentsLoading(isDeploymentsLoading) {
  return {
    type: UPDATE_FIELD,
    data: isDeploymentsLoading,
    key: "isDeploymentsLoading"
  };
}

export function updateIsModelGroupsLoading(isModelGroupsLoading) {
  return {
    type: UPDATE_FIELD,
    data: isModelGroupsLoading,
    key: "isModelGroupsLoading"
  };
}

export function updateIsSchemaMonitorsLoading(isSchemaMonitorLoading) {
  return {
    type: UPDATE_FIELD,
    data: isSchemaMonitorLoading,
    key: "isSchemaMonitorLoading"
  };
}

export function updateIsOptimizersLoading(isOptimizersLoading) {
  return {
    type: UPDATE_FIELD,
    data: isOptimizersLoading,
    key: "isOptimizersLoading"
  };
}

export function updateIsReportsLoading(isReportsLoading) {
  return {
    type: UPDATE_FIELD,
    data: isReportsLoading,
    key: "isReportsLoading"
  };
}

export function updateIsProjectsLoading(isProjectsLoading) {
  return {
    type: UPDATE_FIELD,
    data: isProjectsLoading,
    key: "isProjectsLoading"
  };
}

export function updateIsAnalyticsLoading(isAnalyticsLoading) {
  return {
    type: UPDATE_FIELD,
    data: isAnalyticsLoading,
    key: "isAnalyticsLoading"
  };
}

export function updateIsTemplateLoading(isTemplateLoading) {
  return {
    type: UPDATE_FIELD,
    data: isTemplateLoading,
    key: "isTemplateLoading"
  };
}

export function updateIsDatasetFetching(isDatasetFetching) {
  return {
    type: UPDATE_FIELD,
    data: isDatasetFetching,
    key: "isDatasetFetching"
  };
}

export function updateIsNextLoading(isNextLoading) {
  return {
    type: UPDATE_FIELD,
    data: isNextLoading,
    key: "isNextLoading"
  };
}

export function updateIsOpeningComparison(isOpeningComparison) {
  return {
    type: UPDATE_FIELD,
    data: isOpeningComparison,
    key: "isOpeningComparison"
  };
}

export function updateIsArtifactsLoading(isArtifactsLoading) {
  return {
    type: UPDATE_FIELD,
    data: isArtifactsLoading,
    key: "isArtifactsLoading"
  };
}

export function updateIsFineTuningLoading(isFineTuningLoading) {
  return {
    type: UPDATE_FIELD,
    data: isFineTuningLoading,
    key: "isFineTuningLoading"
  };
}

export function updateIsExampleSetsLoading(isExampleSetsLoading) {
  return {
    type: UPDATE_FIELD,
    data: isExampleSetsLoading,
    key: "isExampleSetsLoading"
  };
}

export function updateIsGoogleAuthed(isGoogleAuthed) {
  return {
    type: UPDATE_FIELD,
    data: isGoogleAuthed,
    key: "isGoogleAuthed"
  };
}

export function updateBlueprintId(blueprintId) {
  return { type: UPDATE_FIELD, data: blueprintId, key: "blueprintId" };
}

export function updateRunId(runId) {
  return { type: UPDATE_FIELD, data: runId, key: "runId" };
}

export function updateIsTableauMode(isTableauMode) {
  return { type: UPDATE_FIELD, data: isTableauMode, key: "isTableauMode" };
}

export function updateIsBusinessMode(isBusinessMode) {
  return { type: UPDATE_FIELD, data: isBusinessMode, key: "isBusinessMode" };
}

export function updateIsDataMode(isDataMode) {
  return { type: UPDATE_FIELD, data: isDataMode, key: "isDataMode" };
}

export function updateIsConsumerMode(isConsumerMode) {
  return { type: UPDATE_FIELD, data: isConsumerMode, key: "isConsumerMode" };
}

export function updateLookerDashboardId(lookerDashboardId) {
  return { type: UPDATE_FIELD, data: lookerDashboardId, key: "lookerDashboardId" };
}

export function updateLastOpenAIChat(lastOpenAIChat) {
  return { type: UPDATE_FIELD, data: lastOpenAIChat, key: "lastOpenAIChat" };
}

export function incrementLastOpenAIChat() {
  return (dispatch, getState) => {
    // const nextOpenAIChat = getLastOpenAIChat(getState()) + 1;
    const nextOpenAIChat = new Date().valueOf();
    dispatch(updateLastOpenAIChat(nextOpenAIChat));
  };
}

export function updateShouldSwitchToGPT(shouldSwitchToGPT) {
  return { type: UPDATE_FIELD, data: shouldSwitchToGPT, key: "shouldSwitchToGPT" };
}

export function updateCurrentRunId(currentRunId) {
  return (dispatch, getState) => {
    if (getCurrentRunId(getState()) !== currentRunId) {
      return dispatch({
        type: UPDATE_FIELD,
        data: currentRunId,
        key: "currentRunId"
      });
    }
  };
}

export function updateCurrentProjectId(currentProjectId) {
  return (dispatch, getState) => {
    if (getCurrentProjectId(getState()) !== currentProjectId) {
      return dispatch({
        type: UPDATE_FIELD,
        data: currentProjectId,
        key: "currentProjectId"
      });
    }
  };
}

export function updateIsCreatingExecutor(isCreatingExecutor) {
  return { type: UPDATE_FIELD, data: isCreatingExecutor, key: "isCreatingExecutor" };
}

export function updateIsCreatingFocusedDataset(isCreatingFocusedDataset) {
  return { type: UPDATE_FIELD, data: isCreatingFocusedDataset, key: "isCreatingFocusedDataset" };
}

export function updateIsCreatingStepByStepAgent(isCreatingStepByStepAgent) {
  return { type: UPDATE_FIELD, data: isCreatingStepByStepAgent, key: "isCreatingStepByStepAgent" };
}

export function addPendingDeployment(deployId) {
  return (dispatch, getState) => {
    const pendingDeployments = getPendingDeployments(getState());

    pendingDeployments[deployId] = true;

    return updatePendingDeployments(pendingDeployments);
  };
}

export function removePendingDeployment(deployId) {
  return (dispatch, getState) => {
    const pendingDeployments = getPendingDeployments(getState());

    pendingDeployments[deployId] = false;

    return updatePendingDeployments(pendingDeployments);
  };
}

export function updateHoverDataset(id) {
  return updateHoverItem(id, "DATASET");
}

export function updateHoverRun(id) {
  return updateHoverItem(id, "RUN");
}

export function updateHoverBlueprint(id) {
  return updateHoverItem(id, "BLUEPRINT");
}

export function updateHover(id, type) {
  return updateHoverItem(id, type);
}

export function updateIsLeftPanelOpen(isLeftPanelOpen) {
  return { type: UPDATE_FIELD, data: isLeftPanelOpen, key: "isLeftPanelOpen" };
}

export function updateHoverItem(id, hoverType) {
  return (dispatch, getState) => {
    if (viewReducer.getType(getState()) === "all") {
      return dispatch({
        type: UPDATE_FIELD,
        data: id === undefined ? undefined : { id: id, hoverType: hoverType },
        key: "hoverItem"
      });
    }
  };
}

export function setFlagsForUser() {
  return (dispatch, getState) => {
    const state = getState();
    const tableauMode = isEnabled("tableau");
    const businessMode = isEnabled("aible-business");
    const dataMode = isEnabled("aible-data");

    if (tableauMode !== getIsTableauMode(state)) {
      dispatch(updateIsTableauMode(tableauMode));
    }
    if (businessMode !== getIsBusinessMode(state)) {
      dispatch(updateIsBusinessMode(businessMode));
    }
    if (dataMode !== getIsDataMode(state)) {
      dispatch(updateIsDataMode(dataMode));
    }
  };
}

export default globalReducer;
